// =============================================================================
// SaaS-Admin › Overview
// Live data from GET /saastracker/admin/kpis + /admin/tenants.
//
// The mock led with MRR and "overdue tenants". Neither exists here: credit is
// prepaid, so there is no recurring subscription price and an invoice is a
// receipt for money already taken — nothing can fall overdue. What replaces them
// is revenue actually captured, and the unspent credit still sitting in wallets,
// which is the real liability on the other side of that revenue.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../services/adminDataService';
import { AdminRoute } from '../SaasAdmin';

/** How many of the highest-utilisation tenants to surface. */
const AT_RISK_COUNT = 6;

// ---- formatting -------------------------------------------------------------

const fmtInt = (n: number | null | undefined): string => (n ?? 0).toLocaleString('en-US');

const fmtMoney = (n: number | null | undefined, ccy: string): string =>
  `${(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${ccy}`;

const fmtCredits = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const fmtDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

/** Same thresholds as the app's own banner: 90 critical, 75 low. */
const barClass = (pctUsed: number): string =>
  pctUsed >= 90 ? 'danger' : pctUsed >= 75 ? 'warn' : '';

// ---- page -------------------------------------------------------------------

export const AdminOverview: React.FC<{ onNavigate: (r: AdminRoute) => void }> = ({ onNavigate }) => {
  const [kpis, setKpis] = useState<api.AdminKpiResponse | null>(null);
  const [atRisk, setAtRisk] = useState<api.AdminTenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const today = useRef(new Date()).current;

  const flash = useCallback((m: string) => {
    setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 4000);
  }, []);
  const fail = useCallback((e: any) => {
    setMsg(''); setErr(e?.message ?? String(e));
  }, []);

  useEffect(() => {
    if (!showHelp) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowHelp(false); };
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setShowHelp(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showHelp]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      // The tenant list is the same endpoint the Tenants page uses, sorted by
      // utilisation — so the two screens can never disagree about who is at risk.
      const [k, t] = await Promise.all([
        api.getKpis(),
        api.getTenants({ sort: 'usage', pageSize: 20 }),
      ]);
      setKpis(k);
      setAtRisk(t.items.filter(x => x.totalUsed > 0).slice(0, AT_RISK_COUNT));
    } catch (e) {
      fail(e);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  /** Evaluates this tenant now and sends whatever the engine says is due. */
  const sendReminder = async (t: api.AdminTenantRow) => {
    setBusyId(t.id);
    try {
      const r = await api.runTenantReminders(t.id);
      flash(r.sent.length
        ? `Sent to ${t.name}: ${r.sent.join(', ')}`
        : `Nothing due for ${t.name} — every reminder for their current usage has already gone out.`);
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };

  const ccy = kpis?.currency ?? 'USD';

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Admin overview</h1>
          <p className="ov-header__sub">At-a-glance health of every tenant on Logipulse.</p>
        </div>
        <div className="ov-header__actions">
          <span className="ov-header__date">
            <span className="material-icons">today</span>
            {fmtDate(today)}
          </span>
          <button className="btn-small" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>

          <div className="rem-help" ref={helpRef}>
            <button
              type="button"
              className="rem-help__btn"
              aria-expanded={showHelp}
              aria-label="How these numbers work"
              title="How these numbers work"
              onClick={() => setShowHelp(v => !v)}
            >
              <span className="material-icons">help_outline</span>
            </button>
            {showHelp && (
              <div className="rem-help__panel" role="dialog" aria-label="How these numbers work">
                <button
                  type="button" className="rem-help__close" aria-label="Close"
                  onClick={() => setShowHelp(false)}
                >
                  <span className="material-icons">close</span>
                </button>
                <p>
                  Credit is <strong>prepaid</strong>, so there is no MRR and nothing can be
                  overdue — an invoice is a receipt for money already taken. Revenue is
                  completed gateway purchases, dated by when the capture settled.
                </p>
                <p>
                  <strong>Credit in wallets</strong> is the other side of that revenue: money
                  you have been paid but not yet earned, owed back as service. Utilisation is
                  lifetime used ÷ granted, which is what decides who is about to run dry —
                  not a monthly quota.
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {(msg || err) && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46',
          border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}`,
        }}>
          {err || msg}
          {err && <button className="btn-small" style={{ marginLeft: 10 }} onClick={load}>Retry</button>}
        </div>
      )}

      {loading && !kpis && (
        <div className="empty">
          <span className="material-icons">hourglass_empty</span>
          Loading overview…
        </div>
      )}

      {kpis && (
        <>
          <section className="ov-kpis">
            <div className="kpi">
              <div className="kpi__icon kpi__icon--blue"><span className="material-icons">apartment</span></div>
              <div className="kpi__main">
                <div className="kpi__num">{fmtInt(kpis.totalTenants)}</div>
                <div className="kpi__label">Tenants</div>
                <div className="kpi__sub">
                  {fmtInt(kpis.activeTenants)} active · {fmtInt(kpis.signupsThisMonth)} new this month
                </div>
              </div>
            </div>

            <div className="kpi">
              <div className="kpi__icon kpi__icon--green"><span className="material-icons">paid</span></div>
              <div className="kpi__main">
                <div className="kpi__num">{fmtMoney(kpis.revenueThisMonth, ccy)}</div>
                <div className="kpi__label">Revenue this month</div>
                <div className="kpi__sub">
                  {fmtInt(kpis.topUpsThisMonth)} top-up{kpis.topUpsThisMonth === 1 ? '' : 's'} captured
                </div>
              </div>
            </div>

            <div className="kpi">
              <div className="kpi__icon kpi__icon--warn"><span className="material-icons">warning</span></div>
              <div className="kpi__main">
                <div className="kpi__num">{fmtInt(kpis.tenantsNearingLimit)}</div>
                <div className="kpi__label">Nearing their limit</div>
                <div className="kpi__sub">
                  80%+ consumed · {fmtInt(kpis.tenantsExhausted)} already dry
                </div>
              </div>
            </div>

            <div className="kpi">
              <div className="kpi__icon kpi__icon--purple"><span className="material-icons">science</span></div>
              <div className="kpi__main">
                <div className="kpi__num">{fmtInt(kpis.trialTenants)}</div>
                <div className="kpi__label">On trial</div>
                <div className="kpi__sub">{fmtInt(kpis.trialsEndingSoon)} ending soon</div>
              </div>
            </div>
          </section>

          {/* Secondary figures — real, but not what you open the page for. */}
          <section className="ov-strip">
            <button className="ov-strip__item" onClick={() => onNavigate('usage')}>
              <span className="ov-strip__num">{fmtCredits(kpis.walletBalanceTotal)}</span>
              <span className="ov-strip__label">Credit in wallets</span>
            </button>
            <button className="ov-strip__item" onClick={() => onNavigate('usage')}>
              <span className="ov-strip__num">{kpis.averageUtilization}%</span>
              <span className="ov-strip__label">Average utilisation</span>
            </button>
            <button className="ov-strip__item" onClick={() => onNavigate('invoices')}>
              <span className="ov-strip__num">{fmtMoney(kpis.revenueLast30Days, ccy)}</span>
              <span className="ov-strip__label">Revenue · last 30 days</span>
            </button>
            <button className="ov-strip__item" onClick={() => onNavigate('invoices')}>
              <span className="ov-strip__num">{fmtMoney(kpis.revenueAllTime, ccy)}</span>
              <span className="ov-strip__label">Revenue · all time</span>
            </button>
            <button
              className={`ov-strip__item${kpis.openTopUps + kpis.failedTopUps > 0 ? ' ov-strip__item--flag' : ''}`}
              onClick={() => onNavigate('payments')}
            >
              <span className="ov-strip__num">{fmtInt(kpis.openTopUps + kpis.failedTopUps)}</span>
              <span className="ov-strip__label">Top-ups unfinished</span>
            </button>
          </section>

          <section className="ov-section">
            <div className="ov-section__head">
              <h2 className="ov-section__title">Tenants closest to running out</h2>
              <button className="ov-section__link" onClick={() => onNavigate('usage')}>View all →</button>
            </div>

            {atRisk.length === 0 ? (
              <div className="viz-empty">No tenant has consumed any credit yet.</div>
            ) : (
              <div className="alert-list">
                {atRisk.map(t => (
                  <div key={t.id} className={`alert-row${t.pctUsed >= 90 ? ' alert-row--critical' : ''}`}>
                    <div className="alert-row__main">
                      <div className="alert-row__name">
                        {t.name}
                        <span className={`plan-tag plan-tag--${t.plan ? t.plan.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'none'}`}>
                          {t.plan ? t.plan.toUpperCase() : 'NONE'}
                        </span>
                        <span className={`status-pill status-pill--${t.status.toLowerCase()}`}>{t.status}</span>
                      </div>
                      <div className="alert-row__domain">{t.email || 'no email on file'}</div>
                    </div>

                    <div className="alert-row__usage">
                      <div className="alert-row__usage-num">{t.pctUsed}%</div>
                      <div className="alert-row__usage-bar">
                        <div
                          className={barClass(t.pctUsed)}
                          style={{ width: `${Math.min(100, Math.max(0, t.pctUsed))}%` }}
                        />
                      </div>
                      <div className="alert-row__usage-meta">
                        {fmtCredits(t.totalUsed)} / {fmtCredits(t.totalGranted)} · {fmtCredits(t.balance)} left
                      </div>
                    </div>

                    <div className="alert-row__actions">
                      <button
                        className="btn-small"
                        onClick={() => sendReminder(t)}
                        disabled={busyId === t.id}
                        title="Evaluate this tenant now and send whatever the engine says is due"
                      >
                        <span className="material-icons">campaign</span>
                        {busyId === t.id ? 'Sending…' : 'Send reminder'}
                      </button>
                      <button className="btn-small btn-small--ghost" onClick={() => onNavigate('tenants')}>
                        View tenant
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ov-quicklinks">
            <button className="quick" onClick={() => onNavigate('tenants')}>
              <span className="material-icons">apartment</span> Manage tenants
            </button>
            <button className="quick" onClick={() => onNavigate('reminders')}>
              <span className="material-icons">campaign</span> Reminder templates
            </button>
            <button className="quick" onClick={() => onNavigate('payments')}>
              <span className="material-icons">paid</span> Unfinished top-ups
            </button>
            <button className="quick" onClick={() => onNavigate('invoices')}>
              <span className="material-icons">receipt_long</span> Recent invoices
            </button>
          </section>
        </>
      )}
    </div>
  );
};
